import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { useLocation } from 'react-router-dom';
import logoFull from './images/ThiranX Logo -.png';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SplashScreen from './components/SplashScreen';
import { BotProvider } from './context/BotContext';
import BotCompanion, { BotFooterInvite } from './components/bot/BotCompanion';
import ThiranXAI from './pages/ThiranXAI';
import Home from './pages/Home';
import About from './pages/About';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Apply from './pages/Apply';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyOtp from './pages/VerifyOtp';
import StudentDashboard from './pages/StudentDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminApplications from './pages/AdminApplications';
import AdminApplicationDetail from './pages/AdminApplicationDetail';
import AdminCourses from './pages/AdminCourses';
import AdminStudents from './pages/AdminStudents';
import AdminMicrointernships from './pages/AdminMicrointernships';
import AdminAnnouncement from './pages/AdminAnnouncement';
import Dsa from './pages/Dsa';
import DsaWorkspace from './pages/DsaWorkspace';
// Bespoke 3-topic visual pilot (Blood Relations / SI-CI / Seating) — shelved for
// now in favor of the MCQ-based AptitudePractice below, kept here to reuse later.
// import Aptitude from './pages/Aptitude';
import AptitudeWorkspace from './pages/AptitudeWorkspace';
import AdminDsa from './pages/AdminDsa';
import AdminAptitude from './pages/AdminAptitude';
import AdminCardPricing from './pages/AdminCardPricing';
import VerifyCertificate from './pages/VerifyCertificate';
import Internships from './pages/Internships';
import Services from './pages/Services';
import Contact from './pages/Contact';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Microinternships from './pages/Microinternships';
import MicrointernshipTask from './pages/MicrointernshipTask';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      height: '100vh', gap: 16, background: 'var(--bg)', color: 'var(--muted)',
      fontFamily: "'Satoshi','DM Sans',sans-serif", fontSize: 14, fontWeight: 600,
    }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(127,0,255,0.2)', borderTopColor: '#7F00FF', animation: 'spin 0.8s linear infinite' }} />
      Loading...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" />;
  return children;
};

// Scrolls to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isAuthPage = /^\/(login|register)\/?$/.test(pathname);

  React.useEffect(() => {
    if (!isAuthPage) return undefined;
    document.documentElement.classList.add('auth-route-active');
    document.body.classList.add('auth-route-active');
    window.scrollTo(0, 0);

    return () => {
      document.documentElement.classList.remove('auth-route-active');
      document.body.classList.remove('auth-route-active');
    };
  }, [isAuthPage]);

  return (
    <>
      <ScrollToTop />
      {isAuthPage ? (
        <header className="auth-site-header">
          <Link to="/" aria-label="ThiranX home">
            <img src={logoFull} alt="ThiranX" />
          </Link>
        </header>
      ) : <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/ai" element={<ThiranXAI />} />
        <Route path="/about" element={<About />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/:id" element={<CourseDetail />} />
        <Route path="/internships" element={<Internships />} />
        <Route path="/services" element={<Services />} />
        <Route path="/microinternships" element={<Microinternships />} />
        <Route path="/microinternship-task/:id" element={<MicrointernshipTask />} />
        <Route path="/dsa" element={<Dsa />} />
        <Route path="/dsa-workspace/:id" element={<DsaWorkspace />} />
        {/* <Route path="/aptitude" element={<Aptitude />} /> */}
        <Route path="/aptitude-workspace/:id" element={<AptitudeWorkspace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/apply/:courseId" element={<ProtectedRoute><Apply /></ProtectedRoute>} />
        <Route path="/login" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} /> : <Register />} />
        <Route path="/verify-otp" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} /> : <VerifyOtp />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/applications" element={<ProtectedRoute adminOnly><AdminApplications /></ProtectedRoute>} />
        <Route path="/admin/applications/:id" element={<ProtectedRoute adminOnly><AdminApplicationDetail /></ProtectedRoute>} />
        <Route path="/admin/courses" element={<ProtectedRoute adminOnly><AdminCourses /></ProtectedRoute>} />
        <Route path="/admin/students" element={<ProtectedRoute adminOnly><AdminStudents /></ProtectedRoute>} />
        <Route path="/admin/microinternships" element={<ProtectedRoute adminOnly><AdminMicrointernships /></ProtectedRoute>} />
        <Route path="/admin/announcement" element={<ProtectedRoute adminOnly><AdminAnnouncement /></ProtectedRoute>} />
        <Route path="/admin/dsa" element={<ProtectedRoute adminOnly><AdminDsa /></ProtectedRoute>} />
        <Route path="/admin/aptitude" element={<ProtectedRoute adminOnly><AdminAptitude /></ProtectedRoute>} />
        <Route path="/admin/card-pricing" element={<ProtectedRoute adminOnly><AdminCardPricing /></ProtectedRoute>} />
        <Route path="/verify" element={<VerifyCertificate />} />
        <Route path="/verify/:certId" element={<VerifyCertificate />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {!isAuthPage && <BotFooterInvite />}
      {!isAuthPage && <Footer />}
      {!isAuthPage && <BotCompanion />}
    </>
  );
};

// Module-level flag: resets on every full page load (reload), but stays
// false across in-app React re-renders so splash never replays mid-session.
let _splashShown = false;

function App() {
  // Show splash on every hard reload; skip during in-app navigation
  const [showSplash, setShowSplash] = useState(() => {
    if (_splashShown) return false;
    return true;
  });

  const handleSplashComplete = () => {
    _splashShown = true;
    setShowSplash(false);
  };

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
          <Router>
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: 'var(--bg2)',
                  color: 'var(--text)',
                  border: '1px solid var(--border-strong)',
                  fontFamily: "'Satoshi','DM Sans',sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                },
                success: { iconTheme: { primary: '#0dba79', secondary: 'var(--bg2)' } },
                error: { iconTheme: { primary: '#f04155', secondary: 'var(--bg2)' } },
              }}
            />
            <BotProvider><AppRoutes /></BotProvider>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
